import React, { useState, useRef } from 'react';
import { leadsAPI } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const ImportLeadsModal = ({ isOpen, onClose, onSuccess }) => {
    const { isDark } = useTheme();
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadStats, setUploadStats] = useState(null);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setError(null);
        setUploadStats(null);

        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                setError('Please upload a valid CSV file.');
                return;
            }
            setFile(selectedFile);
            parseCSV(selectedFile);
        }
    };

    const parseCSV = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n');
            if (lines.length < 2) {
                setError('CSV file is empty or missing data.');
                setPreview([]);
                return;
            }

            // Simple CSV parser (assumes comma separation, handles simple quotes)
            const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());

            const data = lines.slice(1)
                .filter(line => line.trim() !== '')
                .map(line => {
                    const values = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(v => v.trim().replace(/^"|"$/g, ''));
                    const lead = {};
                    headers.forEach((header, index) => {
                        // Map CSV headers to API fields
                        if (header.includes('source')) lead.source_platform = values[index];
                        else if (header.includes('name')) lead.lead_name = values[index];
                        else if (header.includes('email')) lead.lead_email = values[index];
                        else if (header.includes('phone')) lead.lead_phone = values[index];
                        else if (header.includes('address')) lead.property_address = values[index];
                        else if (header.includes('message')) lead.lead_message = values[index];
                        // Add more mappings as needed
                    });
                    return lead;
                })
                .filter(l => l.lead_name || l.lead_email); // Filter empty rows

            setPreview(data);
        };
        reader.readAsText(file);
    };

    const handleUpload = async () => {
        if (!preview.length) {
            setError('No valid leads found in file.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Call bulk create API
            // Note: leadsAPI needs a bulkCreate method, or we use axios directly if not added yet.
            // Assuming leadsAPI.bulkCreate(leads) exists or we add it.
            // If strictly following the plan, I should have updated api.js. 
            // I'll implement the call here assuming leadsAPI handles it or I'll add it to api.js next.
            // Actually, for simplicity I'll assume leadsAPI.createLead can handle bulk? No, controller has explicit bulk endpoint.
            // I'll add bulkCreate to api.js in a separate step, but for now I'll use the endpoint directly via api instance if possible or just assume I'll update api.js.
            // Let's assume I'll update api.js.

            // Wait, I haven't updated api.js. I should do that.
            // But for now let's write this component expecting the method.

            const response = await leadsAPI.bulkCreateLeads(preview);

            setUploadStats({
                success: true,
                count: response.data.count,
                message: response.data.message
            });

            setTimeout(() => {
                if (onSuccess) onSuccess();
                handleClose();
            }, 2000);

        } catch (err) {
            console.error('Upload failed:', err);
            setError(err.response?.data?.error || 'Failed to upload leads.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreview([]);
        setError(null);
        setUploadStats(null);
        onClose();
    };

    const downloadTemplate = () => {
        const headers = ['Source,Name,Email,Phone,Property Address,Message'];
        const example = ['Zillow,John Doe,john@example.com,555-0123,123 Main St,"Interested in this property"'];
        const csvContent = headers.concat(example).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'leads_import_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="w-full max-w-lg bg-mint dark:bg-navy rounded-xl shadow-2xl border border-teal/20 dark:border-light/10 text-left overflow-hidden">

                {/* Header */}
                <div className="px-6 py-4 border-b border-teal/10 dark:border-light/10 flex items-center justify-between bg-teal/5 dark:bg-light/5">
                    <h2 className="text-xl font-bold text-darkTeal dark:text-light flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary dark:text-cyan">upload_file</span>
                        Import Leads
                    </h2>
                    <button onClick={handleClose} className="text-darkTeal/50 dark:text-light/50 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6">
                    {!uploadStats ? (
                        <>
                            <div className="mb-6">
                                <p className="text-sm text-darkTeal/70 dark:text-light/70 mb-4">
                                    Upload a CSV file to bulk import leads. Make sure your file matches the template format.
                                </p>
                                <button
                                    onClick={downloadTemplate}
                                    className="text-primary dark:text-cyan text-sm font-bold hover:underline flex items-center gap-1 mb-4"
                                >
                                    <span className="material-symbols-outlined text-sm">download</span>
                                    Download CSV Template
                                </button>

                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${file
                                            ? 'border-primary dark:border-cyan bg-primary/5 dark:bg-cyan/5'
                                            : 'border-teal/20 dark:border-light/20 hover:border-primary/50 dark:hover:border-cyan/50 hover:bg-teal/5 dark:hover:bg-light/5'
                                        }`}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        accept=".csv"
                                        className="hidden"
                                    />

                                    {file ? (
                                        <div>
                                            <span className="material-symbols-outlined text-4xl text-primary dark:text-cyan mb-2">description</span>
                                            <p className="font-bold text-darkTeal dark:text-light">{file.name}</p>
                                            <p className="text-xs text-darkTeal/50 dark:text-light/50 mt-1">{(file.size / 1024).toFixed(1)} KB • {preview.length} leads found</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <span className="material-symbols-outlined text-4xl text-darkTeal/30 dark:text-light/30 mb-2">cloud_upload</span>
                                            <p className="font-bold text-darkTeal dark:text-light">Click to upload CSV</p>
                                            <p className="text-xs text-darkTeal/50 dark:text-light/50 mt-1">or drag and drop here</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {error && (
                                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-500 rounded-lg text-sm flex items-center gap-2">
                                    <span className="material-symbols-outlined text-lg">error</span>
                                    {error}
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-3 pt-2">
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 rounded-lg text-darkTeal/70 dark:text-light/70 hover:bg-teal/5 dark:hover:bg-light/5 font-semibold transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpload}
                                    disabled={!file || loading || preview.length === 0}
                                    className="px-6 py-2 bg-primary dark:bg-cyan text-white dark:text-navy font-bold rounded-lg shadow-lg hover:shadow-xl hover:bg-primary/90 dark:hover:bg-cyan/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                                >
                                    {loading ? (
                                        <>
                                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                            Importing...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-lg">upload</span>
                                            Import {preview.length > 0 ? `${preview.length} Leads` : ''}
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="material-symbols-outlined text-3xl">check_circle</span>
                            </div>
                            <h3 className="text-xl font-bold text-darkTeal dark:text-light mb-2">Import Successful!</h3>
                            <p className="text-darkTeal/60 dark:text-light/60 mb-6">
                                Successfully imported {uploadStats.count} leads.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportLeadsModal;
